// Extend Window interface for auto-insert timestamp and chord flags
declare global {
  interface Window {
    __autoInsertTimestamp?: boolean;
    __autoInsertChord?: boolean;
  }
}

interface PlayerSwitchingConfig {
  audioElements: Map<number, HTMLAudioElement>;
  selectedPlayer: number;
  onPlayerSelect: (playerNumber: number) => void;
  isBlindTestMode?: boolean;
  reverse?: boolean;
}

interface TransportControlsConfig {
  isPlaying: boolean;
  onPlayPause: () => void;
  onForward: (seconds: number) => void;
  onRewind: (seconds: number) => void;
  onRewindToLastStart: () => void;
  onLoop: () => void;
  onBlindTest: () => void;
  onSyncPoint?: () => void;
  onJumpToSyncPoints?: () => void;
  onClearSyncPoints?: () => void;
  onRemovePlayer?: () => void;
  onToggleSelectedBlindTestIncluded?: () => void;
  onToggleAllBlindTestIncluded?: () => void;
  onImport?: () => void;
  onToggleMono?: () => void;
  onToggleFilter?: () => void;
  onToggleDim?: () => void;
  onWaveformSync?: () => void;
  onCreateRegion?: () => void;
}

export const handlePlayerSwitch = (config: PlayerSwitchingConfig) => {
  const { audioElements, selectedPlayer, onPlayerSelect, isBlindTestMode, reverse } = config;
  
  if (isBlindTestMode) {
    // In blind test mode, we want to trigger the randomize function
    // which is handled by the button click handler
    onPlayerSelect(selectedPlayer);
  } else {
    const validPlayers = Array.from(audioElements.keys()).sort((a, b) => a - b);
    if (validPlayers.length === 0) return;
    
    const currentIndex = validPlayers.indexOf(selectedPlayer);
    const nextIndex = reverse 
      ? (currentIndex - 1 + validPlayers.length) % validPlayers.length
      : (currentIndex + 1) % validPlayers.length;
    const nextPlayer = validPlayers[nextIndex];
    
    onPlayerSelect(nextPlayer);
  }
};

export const handleNumberKeyPlayerSelect = (
  key: string,
  loadedFilesCount: number,
  onPlayerSelect: (playerNumber: number) => void
) => {
  const numKey = parseInt(key);
  if (!isNaN(numKey) && numKey >= 1 && numKey <= 9 && numKey <= loadedFilesCount) {
    onPlayerSelect(numKey);
  }
};

export const setupKeyboardHandlers = (
  config: TransportControlsConfig & { playerCount: number },
  onPlayerSelect: (playerNumber: number) => void,
  audioElements: Map<number, HTMLAudioElement>,
  currentPlayer: number,
  isBlindTestMode?: boolean,
  handlePlayerSelect?: (reverse: boolean) => void,
  isEmbed?: boolean
) => {
  // Don't set up keyboard handlers in embed mode
  if (isEmbed) {
    return () => {}; // Return no-op cleanup function
  }

  const { onPlayPause, onForward, onRewind, onRewindToLastStart, onLoop, onBlindTest, onSyncPoint, onJumpToSyncPoints, onRemovePlayer, onToggleSelectedBlindTestIncluded, onToggleAllBlindTestIncluded, onImport, onToggleMono, onToggleFilter, onToggleDim, onWaveformSync, onCreateRegion } = config;

  const handleKeyDown = (event: KeyboardEvent) => {
    // Skip hotkeys when user is editing text
    const target = event.target as HTMLElement;
    const activeElement = document.activeElement as HTMLElement;
    
    if (target instanceof HTMLInputElement || 
        target instanceof HTMLTextAreaElement ||
        (target instanceof HTMLElement && target.contentEditable === 'true') ||
        activeElement instanceof HTMLInputElement ||
        activeElement instanceof HTMLTextAreaElement ||
        (activeElement instanceof HTMLElement && activeElement.contentEditable === 'true')) {
      return;
    }
    
    // Debug all key events for B
    if (event.key.toLowerCase() === 'b') {
      console.log('B key event detected:', {
        key: event.key,
        code: event.code,
        metaKey: event.metaKey,
        shiftKey: event.shiftKey,
        ctrlKey: event.ctrlKey,
        altKey: event.altKey,
        defaultPrevented: event.defaultPrevented,
        target: event.target,
        type: event.type
      });
    }
    
    const actualPlayerCount = audioElements.size;
    
    switch (event.key) {
      case 'Backspace':
      case 'Delete':
        // Don't trigger delete when user is editing text
        if (!(event.target instanceof HTMLInputElement || 
              event.target instanceof HTMLTextAreaElement ||
              (event.target instanceof HTMLElement && event.target.contentEditable === 'true'))) {
          event.preventDefault();
          if (onRemovePlayer && actualPlayerCount > 1) {
            onRemovePlayer();
          }
        }
        break;
      case ' ':
        event.preventDefault();
        onPlayPause();
        break;
      case 'arrowleft':
      case 'ArrowLeft':
        event.preventDefault();
        if (event.ctrlKey && event.altKey && actualPlayerCount === 2) {
          // ⌃⌥←: Return to last played position and switch player (only when exactly 2 players)
          if (onRewindToLastStart) {
            onRewindToLastStart();
          }
          // Switch to the other player
          const players = Array.from(audioElements.keys()).sort((a, b) => a - b);
          const otherPlayer = players.find(p => p !== currentPlayer);
          if (otherPlayer !== undefined) {
            onPlayerSelect(otherPlayer);
          }
        } else if (event.altKey) {
          // Rewind to last start position
          if (onRewindToLastStart) {
            onRewindToLastStart();
          }
        } else if (event.metaKey) {
          // Play from start without affecting playback state
          audioElements.forEach(audio => {
            audio.currentTime = 0;
          });
        } else if (event.shiftKey) {
          onRewind(10);
        } else {
          onRewind(3);
        }
        break;
      case 'arrowright':
      case 'ArrowRight':
        event.preventDefault();
        if (event.shiftKey) {
          onForward(10);
        } else {
          onForward(3);
        }
        break;
      case '/':
      case '?':
        if (actualPlayerCount >= 2) {
          event.preventDefault();
          if (handlePlayerSelect) {
            handlePlayerSelect(event.shiftKey);
          } else {
            handlePlayerSwitch({
              audioElements,
              selectedPlayer: currentPlayer,
              onPlayerSelect,
              isBlindTestMode,
              reverse: event.shiftKey
            });
          }
        }
        break;
      case 'arrowup':
      case 'ArrowUp':
        if (actualPlayerCount >= 2) {
          event.preventDefault();
          if (handlePlayerSelect) {
            handlePlayerSelect(true); // true for reverse direction (like shift+/)
          } else {
            handlePlayerSwitch({
              audioElements,
              selectedPlayer: currentPlayer,
              onPlayerSelect,
              isBlindTestMode,
              reverse: true
            });
          }
        }
        break;
      case 'arrowdown':
      case 'ArrowDown':
        if (actualPlayerCount >= 2) {
          event.preventDefault();
          if (handlePlayerSelect) {
            handlePlayerSelect(false); // false for forward direction (like /)
          } else {
            handlePlayerSwitch({
              audioElements,
              selectedPlayer: currentPlayer,
              onPlayerSelect,
              isBlindTestMode,
              reverse: false
            });
          }
        }
        break;
      case 'l':
        // Only handle plain L key, let browser handle ⌘L (address bar)
        if (!event.metaKey && !event.ctrlKey && !event.altKey) {
          event.preventDefault();
          onLoop();
        }
        break;
      case 'b':
      case 'B':
        event.preventDefault();
        if (event.metaKey && onToggleSelectedBlindTestIncluded) {
          // Cmd+B: Toggle selected player's blind test inclusion
          onToggleSelectedBlindTestIncluded();
        } else if (event.shiftKey && onToggleAllBlindTestIncluded) {
          // Shift+B: Toggle all players' blind test inclusion
          onToggleAllBlindTestIncluded();
        } else {
          // Plain B: Toggle blind test mode
          onBlindTest();
        }
        break;
      case 'a':
        // Only handle plain A key, let browser handle ⌘A (select all)
        if (!event.metaKey && !event.ctrlKey && !event.altKey) {
          event.preventDefault();
          if (onSyncPoint) {
            onSyncPoint();
          }
        }
        break;
      case 's':
      case 'S':
        // Only handle plain S key, let browser handle ⌘S (save page)
        if (!event.metaKey && !event.ctrlKey && !event.altKey) {
          event.preventDefault();
          
          // Normal press - handler will decide between autosync (no anchors) or manual sync (anchors exist)
          if (onJumpToSyncPoints) {
            onJumpToSyncPoints();
          }
        }
        break;
      case 'm':
      case 'M':
        // Only handle plain M key, let browser handle ⌘M (minimize window on Mac)
        if (!event.metaKey && !event.ctrlKey && !event.altKey) {
          event.preventDefault();
          if (onToggleMono) {
            onToggleMono();
          }
        }
        break;
      case 'd':
      case 'D':
        // Only handle plain D key on main player page (when files are loaded)
        // FileImport handles D key on import page using capture phase
        if (!event.metaKey && !event.ctrlKey && !event.altKey && actualPlayerCount > 0) {
          // Only handle if we have audio elements (i.e., we're on the main player page, not import page)
          // FileImport uses capture phase, so if we're on import page, it will handle it first
          event.preventDefault();
          if (onToggleDim) {
            onToggleDim();
          }
        }
        break;
      case 'f':
      case 'F':
        // Only handle plain F key, let browser handle ⌘F (find)
        if (!event.metaKey && !event.ctrlKey && !event.altKey) {
          event.preventDefault();
          if (onToggleFilter) {
            onToggleFilter();
          }
        }
        break;
      case 'n':
      case 'N': {
        // Only handle plain N key, let browser handle ⌘N (new window/tab)
        if (!event.metaKey && !event.ctrlKey && !event.altKey) {
          event.preventDefault();
          // Check if expanded note panel is available first
          const expandedNotePanel = (window as any).__expandedNotePanel;
          if (expandedNotePanel && expandedNotePanel.trackNumber === currentPlayer) {
            // Use the expanded note panel for the current player
            expandedNotePanel.startEditing();
          } else {
            // Fall back to opening notes modal
            const notesButton = document.querySelector(`[data-player-number="${currentPlayer}"] [data-testid="notes-button"]`) as HTMLButtonElement;
            if (notesButton && !notesButton.disabled) {
              notesButton.click();
            }
          }
        }
        break;
      }
      case 'i':
      case 'I': {
        event.preventDefault();
        
        if (event.metaKey) {
          if (event.shiftKey) {
            // ⇧⌘I: Insert textstamp
            const expandedNotePanel = (window as any).__expandedNotePanel;
            if (expandedNotePanel && expandedNotePanel.trackNumber === currentPlayer) {
              // Use the expanded note panel for the current player
              expandedNotePanel.startEditingAndInsertChord();
            } else {
              // Fall back to opening notes modal for selected player and auto-insert textstamp
              const notesButton = document.querySelector(`[data-player-number="${currentPlayer}"] [data-testid="notes-button"]`) as HTMLButtonElement;
              if (notesButton && !notesButton.disabled) {
                // Set a flag to indicate we want to auto-insert textstamp
                console.log('Setting auto-insert textstamp flag and opening notes modal');
                window.__autoInsertChord = true;
                notesButton.click();
              } else {
                console.log('Notes button not found or disabled for player', currentPlayer);
              }
            }
          } else {
            // ⌘I: Insert timestamp
            const expandedNotePanel = (window as any).__expandedNotePanel;
            if (expandedNotePanel && expandedNotePanel.trackNumber === currentPlayer) {
              // Check if the editor is already in editing mode
              if (expandedNotePanel.isEditing) {
                // Editor is already in editing mode, use insertTimestampAtEnd to add new lines if not focused
                expandedNotePanel.insertTimestampAtEnd();
              } else {
                // Not in editing mode, start editing and insert timestamp
                expandedNotePanel.startEditingAndInsertTimestamp();
              }
            } else {
              // Fall back to opening notes modal for selected player and auto-insert timestamp
              const notesButton = document.querySelector(`[data-player-number="${currentPlayer}"] [data-testid="notes-button"]`) as HTMLButtonElement;
              if (notesButton && !notesButton.disabled) {
                // Set a flag to indicate we want to auto-insert timestamp
                console.log('Setting auto-insert timestamp flag and opening notes modal');
                window.__autoInsertTimestamp = true;
                notesButton.click();
              } else {
                console.log('Notes button not found or disabled for player', currentPlayer);
              }
            }
          }
        } else {
          // Plain I: Import files (existing behavior)
          const addFilesButton = document.querySelector('[data-testid="add-files-button"]') as HTMLButtonElement;
          if (addFilesButton && !addFilesButton.disabled) {
            addFilesButton.click();
          } else if (onImport) {
            // Fallback to onImport callback if button not found
            onImport();
          }
        }
        break;
      }
      case 'c':
      case 'C': {
        event.preventDefault();
        
        if (event.metaKey && event.ctrlKey) {
          // ⌃⌘C: Check if expanded note panel is available first
          const expandedNotePanel = (window as any).__expandedNotePanel;
          if (expandedNotePanel && expandedNotePanel.trackNumber === currentPlayer) {
            // Use the expanded note panel for the current player
            expandedNotePanel.startEditingAndInsertChord();
          } else {
            // Fall back to opening notes modal for selected player and auto-insert chord
            const notesButton = document.querySelector(`[data-player-number="${currentPlayer}"] [data-testid="notes-button"]`) as HTMLButtonElement;
            if (notesButton && !notesButton.disabled) {
              // Set a flag to indicate we want to auto-insert chord
              console.log('Setting auto-insert chord flag and opening notes modal');
              window.__autoInsertChord = true;
              notesButton.click();
            } else {
              console.log('Notes button not found or disabled for player', currentPlayer);
            }
          }
        }
        break;
      }
      case 'r':
      case 'R':
        // Only handle plain R key, let browser handle ⌘R (reload)
        if (!event.metaKey && !event.ctrlKey && !event.altKey) {
          event.preventDefault();
          if (onCreateRegion) {
            onCreateRegion();
          }
        }
        break;
      default:
        handleNumberKeyPlayerSelect(event.key, actualPlayerCount, onPlayerSelect);
        break;
    }
  };

  const handleKeyUp = (event: KeyboardEvent) => {
    // Skip hotkeys when user is editing text
    const target = event.target as HTMLElement;
    const activeElement = document.activeElement as HTMLElement;
    
    if (target instanceof HTMLInputElement || 
        target instanceof HTMLTextAreaElement ||
        (target instanceof HTMLElement && target.contentEditable === 'true') ||
        activeElement instanceof HTMLInputElement ||
        activeElement instanceof HTMLTextAreaElement ||
        (activeElement instanceof HTMLElement && activeElement.contentEditable === 'true')) {
      return;
    }

    // S key release no longer needed - removed hold-to-autosync behavior
  };

  window.addEventListener('keydown', handleKeyDown);
  window.addEventListener('keyup', handleKeyUp);
  return () => {
    window.removeEventListener('keydown', handleKeyDown);
    window.removeEventListener('keyup', handleKeyUp);
  };
};
